import { useMemo, useRef, useState } from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import { Plus } from '@factorialco/f0-react/icons/app'
import {
  FinanceSalesDocumentTemplatesSelectorDocument,
  SalesDocumentTemplateForSelectFragment,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import { EntitySelectWrapper } from 'modules/core/components/EntitySelectWrapper'
import LabeledInput from 'modules/core/components/LabeledInput'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useActivities } from 'modules/core/lib/activities/provider'
import i18n from 'modules/core/lib/i18n'

import {
  mapFinanceSalesDocumentTemplates,
  mapToFinanceSalesDocumentTemplateFragment,
} from './mappers'
import {
  FinanceSalesDocumentTemplateSelectorProps,
  FinanceSalesDocumentTemplateSelectorSingle,
  GroupType,
} from './types'

const isFinanceSalesDocumentTemplateSelectorCreationProps = (
  props: FinanceSalesDocumentTemplateSelectorProps
): props is FinanceSalesDocumentTemplateSelectorSingle => {
  return !!props.enableCreate
}

export const FinanceSalesDocumentTemplateSelector = (
  props: FinanceSalesDocumentTemplateSelectorProps
) => {
  const {
    disabled,
    value,
    enableCreate,
    refetchQuery,
    queryParams,
    alwaysOpen,
    defaultOpen,
    width,
    minWidth,
    error,
    mandatory,
    label,
    emptyLabel,
    hideSearchIcon,
    variant = 'f0',
  } = props

  const { openActivity } = useActivities()

  const openCreateSalesDocumentTemplateActivity = () => {
    if (isFinanceSalesDocumentTemplateSelectorCreationProps(props)) {
      openActivity('finance.salesDocumentTemplateCreate', {
        refetchQuery: refetchQuery ?? 'FinanceSalesDocumentTemplatesSelector',
      })
    }
  }
  const [skip, setSkip] = useState<boolean>(!(alwaysOpen || defaultOpen))

  const { loading, data } = useQuery(FinanceSalesDocumentTemplatesSelectorDocument, {
    variables: {
      legalEntityIds: queryParams.legalEntityIds,
      search: queryParams.search,
    },
    skip,
  })

  const financeSalesDocumentTemplates = data?.finance.salesDocumentTemplates

  const onOpen = () => {
    if (skip) setSkip(false)
  }

  const financeSalesDocumentTemplateEntities = useMemo(
    () => mapFinanceSalesDocumentTemplates(financeSalesDocumentTemplates ?? []),
    [financeSalesDocumentTemplates]
  )

  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false)
  const previousSalesDocumentTemplatesRef = useRef<EntitySelectEntity[]>([])

  const selectNewSalesDocumentTemplate = useUnsafeCallback(
    (salesDocumentTemplate: EntitySelectEntity) => {
      props.onSelect(
        mapToFinanceSalesDocumentTemplateFragment(
          salesDocumentTemplate,
          financeSalesDocumentTemplates ?? []
        )
      )
    },
    [props, value, financeSalesDocumentTemplates]
  )

  useUnsafeEffect(() => {
    if (!isInitialLoadComplete && !loading && financeSalesDocumentTemplateEntities.length > 0) {
      setIsInitialLoadComplete(true)
      previousSalesDocumentTemplatesRef.current = financeSalesDocumentTemplateEntities
    }
  }, [loading, financeSalesDocumentTemplateEntities, isInitialLoadComplete])

  useEffect(() => {
    return () => {
      setIsInitialLoadComplete(false)
      previousSalesDocumentTemplatesRef.current = []
    }
  }, [skip])

  useUnsafeEffect(() => {
    if (!isInitialLoadComplete) return

    const hasNewSalesDocumentTemplates =
      financeSalesDocumentTemplateEntities.length > previousSalesDocumentTemplatesRef.current.length
    if (!hasNewSalesDocumentTemplates) {
      previousSalesDocumentTemplatesRef.current = financeSalesDocumentTemplateEntities
      return
    }

    const newSalesDocumentTemplate = financeSalesDocumentTemplateEntities.find(
      (salesDocumentTemplate) =>
        !previousSalesDocumentTemplatesRef.current.some(
          (prev) => prev.id === salesDocumentTemplate.id
        )
    )

    if (newSalesDocumentTemplate) {
      selectNewSalesDocumentTemplate(newSalesDocumentTemplate)
    }

    previousSalesDocumentTemplatesRef.current = financeSalesDocumentTemplateEntities
  }, [financeSalesDocumentTemplateEntities, isInitialLoadComplete, selectNewSalesDocumentTemplate])

  const buildFinanceSalesDocumentTemplateName = (
    selectedValue: SalesDocumentTemplateForSelectFragment
  ) => {
    return selectedValue.name
  }

  const selectedFinanceSalesDocumentTemplates = useMemo(
    () =>
      value
        ? [
            {
              id: value.id,
              name: value ? buildFinanceSalesDocumentTemplateName(value) : '',
            },
          ]
        : [],
    [value, financeSalesDocumentTemplateEntities]
  )

  const selectedFinanceSalesDocumentTemplatesForInput = useMemo(() => {
    const salesDocumentTemplates = Array.isArray(value) ? value : compact([value])

    return salesDocumentTemplates.map((salesDocumentTemplate) => ({
      id: salesDocumentTemplate.id,
      name: value ? buildFinanceSalesDocumentTemplateName(salesDocumentTemplate) : '',
    }))
  }, [value])

  const groupMap: Record<GroupType, EntitySelectEntity[]> = useMemo(
    () => ({
      all: financeSalesDocumentTemplateEntities,
    }),
    [financeSalesDocumentTemplateEntities]
  )

  const selectedGroupMap: Record<GroupType, EntitySelectEntity[]> = useMemo(
    () => ({
      all: selectedFinanceSalesDocumentTemplates ?? [],
    }),
    [selectedFinanceSalesDocumentTemplates]
  )

  const handleOnSelectSingle = (selection: EntitySelectEntity | null) => {
    if (financeSalesDocumentTemplates) {
      props.onSelect(
        selection
          ? mapToFinanceSalesDocumentTemplateFragment(selection, financeSalesDocumentTemplates)
          : null
      )
    }
  }

  const firstFinanceSalesDocumentTemplate = selectedFinanceSalesDocumentTemplates[0]
  const name =
    !!value && firstFinanceSalesDocumentTemplate
      ? firstFinanceSalesDocumentTemplate.name
      : undefined

  return (
    <Box minWidth={minWidth} width={'full'} position="relative">
      <EntitySelectWrapper
        {...props}
        value={props.value}
        label={label ?? i18n.t('finance.sales_document_templates.selector.select_templates')}
        hideLabel={variant === 'gamma'}
        hiddenAvatar
        loading={loading}
        onOpen={onOpen}
        entities={groupMap['all'] || []}
        selectedGroup={'all'}
        selectedGroupedEntities={selectedGroupMap['all']}
        onSelectExpandedElements={() => {}}
        expandedElements={[]}
        selectedEntities={selectedFinanceSalesDocumentTemplates}
        searchPlaceholder={i18n.t('finance.sales_document_templates.selector.search')}
        selectAllLabel={i18n.t('finance.sales_document_templates.selector.select_all')}
        clearLabel={i18n.t('finance.sales_document_templates.selector.clear')}
        selectedLabel={i18n.t('finance.sales_document_templates.selector.selected')}
        notFoundSubtitle={i18n.t('finance.sales_document_templates.selector.not_found.subtitle')}
        notFoundTitle={i18n.t('finance.sales_document_templates.selector.not_found.title')}
        placeholder={i18n.t('finance.sales_document_templates.selector.select_templates')}
        selectedItemsCopy={i18n.t('finance.sales_document_templates.selector.templates_selected')}
        groups={[
          { label: i18n.t('finance.sales_document_templates.selector.groups.none'), value: 'all' },
        ]}
        onGroupChange={() => {}}
        onSelectMultiple={() => {}}
        singleSelector
        onSelect={handleOnSelectSingle}
        minWidth={minWidth}
        width={width}
        actions={
          enableCreate
            ? [
                {
                  label: i18n.t('finance.sales_document_templates.selector.create_new_template'),
                  onClick: openCreateSalesDocumentTemplateActivity,
                  icon: Plus,
                  variant: 'neutral',
                },
              ]
            : undefined
        }
        size="md"
      >
        {variant === 'gamma' && (
          <Box flexDirection="row" width="full">
            <LabeledInput
              aria-label="serial-sequence-selector"
              readonly={disabled}
              error={error}
              mandatory={mandatory}
              label={label}
              value={value?.toString() ?? undefined}
            >
              <Box
                paddingX="s16"
                paddingY="s8"
                flexDirection="row"
                height="s40"
                alignItems="center"
                width="full"
                cursor={disabled ? 'default' : 'pointer'}
              >
                <Box flexDirection="row" width="full" gap="s6">
                  {!hideSearchIcon && (
                    <Icon.Medium icon="search" color={disabled ? 'grey600' : 'grey800'} />
                  )}
                  <Box overflowX="hidden" textAlign="left">
                    <Text.Normal ellipsis>
                      {name ??
                        emptyLabel ??
                        i18n.t('finance.sales_document_templates.selector.empty_label')}
                    </Text.Normal>
                  </Box>
                </Box>
                <Icon.Small icon="caret-down" />
              </Box>
            </LabeledInput>
          </Box>
        )}
      </EntitySelectWrapper>
    </Box>
  )
}
